import type { ReactNode } from "react";

import Logo from "@/components/brand/Logo";
import { cn } from "@/lib/utils";

type StatusPageProps = {
	code: string;
	title: string;
	description: string;
	actions?: ReactNode;
	viewport?: "shell" | "screen";
};

export default function StatusPage({
	code,
	title,
	description,
	actions,
	viewport = "shell",
}: StatusPageProps) {
	return (
		<section
			className={cn(
				"relative isolate flex w-full items-center overflow-hidden px-4 py-6 pb-24 sm:px-6 sm:py-10 md:pb-12",
				viewport === "screen"
					? "min-h-screen"
					: "min-h-[calc(100svh-10rem)] md:min-h-[36rem]",
			)}
		>
			<div className="mx-auto w-full max-w-3xl">
				<div className="relative overflow-hidden rounded-[2rem] p-6 backdrop-blur-xl sm:p-8 md:p-10">
					<div className="mx-auto flex max-w-xl flex-col items-center text-center">
						<Logo
							variant="logo"
							size="xl"
							className="pl-4 text-[2.35rem] opacity-50 sm:text-[2.9rem] md:pl-5"
						/>

						<div className="mt-4 space-y-4 sm:space-y-5">
							<p className="select-none font-black font-heading text-6xl text-foreground/90 tracking-tight sm:text-7xl md:text-[7rem]">
								{code}
							</p>

							<div className="space-y-3">
								<h1 className="text-balance font-semibold text-2xl text-foreground tracking-tight sm:text-4xl">
									{title}
								</h1>
								<p className="mx-auto max-w-lg text-pretty text-muted-foreground text-sm leading-6 sm:text-base sm:leading-7">
									{description}
								</p>
							</div>
						</div>

						{actions ? (
							<div className="mt-8 flex w-full max-w-md flex-col gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:justify-center">
								{actions}
							</div>
						) : null}
					</div>
				</div>
			</div>
		</section>
	);
}
